import { Injectable, Injector, inject } from '@angular/core'; // <-- IMPORT inject
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subject, firstValueFrom, BehaviorSubject, map, Observable, of, filter, take, throwError } from 'rxjs';
import { catchError, takeUntil, finalize, timeout } from 'rxjs/operators';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { PatientRegistrationRequest } from '@/api/proxy/mpi/models/patient-registration-request';
import { LoadingService } from '../../services/loading.service';
import { HealthFindrAnonymousService } from '@/api/proxy/healthFindr/healthFindr-services';
import { ORGAnonymousService } from '@/api/proxy/organization/services/org-anonymous.service';
import { FormRequestModel } from '@/api/proxy/healthFindr/models/form-request-model';
import { ConfigSearchRequestModel } from '@/api/proxy/healthFindr/models/config-search-request-model';
import { TermsConditionsModel } from '@/api/proxy/healthFindr/models/terms-conditions-model';
import { MPIDataService } from '@/api/proxy/mpi/services/mpi-data.service';
import { MedicalSchemeDto } from '@/api/proxy/mpi/models/medical-scheme-dto';
import { InsurerDto } from '@/api/proxy/mpi/models/insurer-dto';
import { CoidClaimManagerDto } from '@/api/proxy/mpi/models/coid-claim-manager-dto';
import { BookRegistrationDto } from '@/api/proxy/mpi/models/book-registration-dto';
import { Patient } from '@/api/proxy/mpi/models/patient';
import { Guarantor } from '@/api/proxy/mpi/models/guarantor';
import { NextOfKin } from '@/api/proxy/mpi/models/next-of-kin';
import { Addresses } from '@/api/proxy/mpi/models/addresses';
import { Address } from '@/api/proxy/mpi/models/address';
import { Phone } from '@/api/proxy/mpi/models/phone';
import { MarketingComms } from '@/api/proxy/mpi/models/marketing-comms';
import { MedicalScheme } from '@/api/proxy/mpi/models/medical-scheme';
import { Coid } from '@/api/proxy/mpi/models/coid';
import { Company } from '@/api/proxy/mpi/models/company';
import { Insurance } from '@/api/proxy/mpi/models/insurance';
import { Gaurdian } from '@/api/proxy/mpi/models/gaurdian';
import { PatientRegistrationRequestDto } from '@/api/proxy/mpi/models/patient-registration-request-dto';
import { bookCompleteBookRegistrationIdPost } from '@/api/proxy/mpi/fn/book/book-complete-book-registration-id-post';
import { DemoService } from '@/services/demo-service/demo.service';
import { environment } from '../../../environments/default/environment';
import { AuthService } from '@/services/auth-service/auth.service';
import { LayoutService } from '../layout/app.layout.service';
import { relationToMainMemberOptions, completedByOptions } from '@/shared/constants/enums';
import { countryCodes } from '@/shared/constants/country-codes';
import { GuarantorMainMember } from '../../pages/patient-info/patien-info.model';

export interface OnboardingForm {
  sectionTitle: string;
  fields: FormlyFieldConfig[];
  orderIndex: number;
}
export interface MedicalAidPlan {
  code: string | null;
  planName: string | null;
}
export interface GroupedMedicalAidScheme {
  name: string;
  plans: MedicalAidPlan[];
}
export interface Insurer {
  code: string | null;
  name: string | null;
}
export interface CoidClaimManager {
  code: string | null;
  name: string | null;
}
export class PatientDetails {
  patientInitials: string | null = null;
  patientFirstName: string | null = null;
  patientSurname: string | null = null;
  patientDateOfBirth: string | null = null;
  email: string | null = null;
  cellNumber: string | null = null;
  cellNumberCountryCode: string | null = null;
  patientIdentifierType: 'id' | 'passport' | null = null;
  patientIdNo: string | null = null;
  patientPassportNo: string | null = null;
  patientMarketingOptIn: boolean | null = null;
  relationshipToGuarantor: string | null = null;
  patientNationality: string | null = null;
  sex: string | null = null;
}
export class OnboardingDataModel {
  requireSignature: boolean = true;
  bookingTenantId: string | null = null;
  affiliatedOrganizationFhirId: string | null = null;
  organizationBranchFhirId: string | null = null;
  gaurentorDetails: GuarantorMainMember | null = null;
  patientDetails: PatientDetails | null = null;
  addressDetails: any | null = null;
  nextOfKinDetails: any | null = null;
  paymentDetails: any | null = null;
  isMainMemberPatient: boolean = true;
  termsAccepted: boolean = false;
  termsVersion: string | null = null;
  termsAcceptedAt: string | null = null;
  guarantorSignatureDataUrl: string | null = null;
  patientSignatureDataUrl: string | null = null;
  isPatientUnder16: boolean | null = null;
  formData: { [key: string]: any } = {};
  sourceInstance: string | null = null;
  externalId: string | null = null;
  bookRegistrationId: string | null = null;
  termsAndConditionsHtml: string | null = null;
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private onboardingInfo: OnboardingDataModel = new OnboardingDataModel();
  private currentStepIndex: number = 0;
  private stepCompletion: boolean[] = Array(7).fill(true);
  public parentFhirId: string = '';
  public logoUri: string = '';
  private _stepsChanged = new Subject<number>();
  private _unsubscribeAll = new Subject<void>();
  private isFetchingHostConfig = false;
  private hostConfigPromise: Promise<string> | null = null;
  stepsChanged$ = this._stepsChanged.asObservable();
  private _onboardingFormsCache: OnboardingForm[] = [];
  private _onboardingFormsLoaded = new BehaviorSubject<OnboardingForm[]>([]);
  public onboardingForms$ = this._onboardingFormsLoaded.asObservable();
  private _initialDataProcessed = new BehaviorSubject<boolean>(false);
  public initialDataProcessed$ = this._initialDataProcessed.asObservable().pipe(filter((processed) => processed));
  private mpiRootUrl = environment.mpiBaseUrl;

  private readonly HOST_PARENT_ORG_ID_KEY = 'host_parent_org_id';
  public readonly DEFAULT_COUNTRY_ISO_CODE = 'ZA';
  public readonly DEFAULT_COUNTRY_CODE = '27';

  // AuthService, Router, and LayoutService have been removed from the constructor
  constructor(
    protected _orgAnonymousService: ORGAnonymousService,
    protected _healthFindrAnonymousService: HealthFindrAnonymousService,
    protected _loadingService: LoadingService,
    private http: HttpClient,
    private injector: Injector,
    private mpiDataService: MPIDataService
  ) {}

  public async GetHostConfig(): Promise<string> {
    if (this.parentFhirId) {
      return this.parentFhirId;
    }
    const cachedId = localStorage.getItem(this.HOST_PARENT_ORG_ID_KEY);
    if (cachedId) {
      this.parentFhirId = cachedId;
      return cachedId;
    }
    if (this.hostConfigPromise) {
      const id = await this.hostConfigPromise;
      if (!id) throw new Error('Previous host config fetch failed.');
      return id;
    }

    this.isFetchingHostConfig = true;
    this.hostConfigPromise = firstValueFrom(
      this._orgAnonymousService.anonymousParentEntityIdForHostnameGet({ hostName: window.location.host }).pipe(
        timeout(7000),
        takeUntil(this._unsubscribeAll),
        map((result) => {
          if (!result) {
            throw new Error('Host configuration API returned empty result.');
          }
          this.parentFhirId = result;
          localStorage.setItem(this.HOST_PARENT_ORG_ID_KEY, result);
          this.isFetchingHostConfig = false;
          this.hostConfigPromise = null;
          return result;
        }),
        catchError((err) => {
          this.isFetchingHostConfig = false;
          this.parentFhirId = '';
          this.hostConfigPromise = null;
          return throwError(() => new Error('Failed to get host configuration: ' + (err.message || err.statusText || 'Unknown API Error')));
        })
      )
    ).catch((error) => {
      this.parentFhirId = '';
      this.isFetchingHostConfig = false;
      this.hostConfigPromise = null;
      throw error;
    });

    return this.hostConfigPromise;
  }

  private formatDateToSlash(dateStr: string | null | undefined): string | null {
    if (!dateStr) return null;
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (!isNaN(year) && year > 1000 && !isNaN(month) && month >= 1 && month <= 12 && !isNaN(day) && day >= 1 && day <= 31) {
        try {
          const d = new Date(year, month - 1, day);
          if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
            return `${parts[0]}/${parts[1]}/${parts[2]}`;
          }
        } catch (e) {}
      }
    }

    if (dateStr.includes('/')) {
      const slashParts = dateStr.split('/');
      if (slashParts.length === 3) {
        const year = parseInt(slashParts[0], 10);
        const month = parseInt(slashParts[1], 10);
        const day = parseInt(slashParts[2], 10);
        if (!isNaN(year) && year > 1000 && !isNaN(month) && month >= 1 && month <= 12 && !isNaN(day) && day >= 1 && day <= 31) {
          try {
            const d = new Date(year, month - 1, day);
            if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
              return dateStr;
            }
          } catch (e) {}
        }
      }
    }
    return null;
  }

  private formatDateToDash(dateStr: string | null | undefined): string | null {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      if (!isNaN(year) && year > 1000 && !isNaN(month) && month >= 1 && month <= 12 && !isNaN(day) && day >= 1 && day <= 31) {
        try {
          const d = new Date(year, month - 1, day);
          if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
          }
        } catch (e) {}
      }
    }

    if (dateStr.includes('-')) {
      const dashParts = dateStr.split('-');
      if (dashParts.length === 3) {
        const year = parseInt(dashParts[0], 10);
        const month = parseInt(dashParts[1], 10);
        const day = parseInt(dashParts[2], 10);
        if (!isNaN(year) && year > 1000 && !isNaN(month) && month >= 1 && month <= 12 && !isNaN(day) && day >= 1 && day <= 31) {
          try {
            const d = new Date(year, month - 1, day);
            if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
              return dateStr;
            }
          } catch (e) {}
        }
      }
    }
    return null;
  }

 public async processInitialGuidData(dto: BookRegistrationDto): Promise<void> {
    this.clearOnboardingData();
    const requestData = dto.bookRegistrationRequest;
    const router = this.injector.get(Router);
    if (!requestData) {
      this._initialDataProcessed.next(false);
      return;
    }

    let hostId: string | null = null;
    try {
      hostId = await this.GetHostConfig();
    } catch (error: any) {
      this._initialDataProcessed.next(false);

      router.navigate(['/invalid-link'], { queryParams: { reason: 'host-config-error', message: error?.message || 'Unknown error' } });
      return;
    }

    this.onboardingInfo.bookRegistrationId = dto.bookRegistrationId ?? null;
    this.onboardingInfo.sourceInstance = requestData.sourceInstance ?? null;
    this.onboardingInfo.externalId = requestData.externalId ?? null;
    this.onboardingInfo.bookingTenantId = requestData.tenantId ?? null;

    this.onboardingInfo.requireSignature = dto.requireSignature ?? true;
   

    if (requestData.guarantor) {
      this.onboardingInfo.gaurentorDetails = this.mapGuarantorToPersonalDetails(requestData.guarantor);
      if (requestData.marketingComms && this.onboardingInfo.gaurentorDetails) {
        this.onboardingInfo.gaurentorDetails.marketingCommsGaurantor = requestData.marketingComms.guarantor ?? null;
      }
    } else {
      this.onboardingInfo.gaurentorDetails = null;
    }

    const guarantorId = requestData.guarantor?.southAfricanIdNumber;
    const patientId = requestData.patient?.southAfricanIdNumber;
    const guarantorPassport = requestData.guarantor?.passportNumber;
    const patientPassport = requestData.patient?.passportNumber;

    if (requestData.patient) {
      const isSamePerson =
        (guarantorId && patientId && guarantorId === patientId) ||
        (guarantorPassport && patientPassport && guarantorPassport === patientPassport) ||
        !requestData.guarantor;

      this.onboardingInfo.isMainMemberPatient = false;
      this.onboardingInfo.patientDetails = this.mapPatientToPatientDetails(requestData.patient);
      if (requestData.marketingComms && this.onboardingInfo.patientDetails) {
        this.onboardingInfo.patientDetails.patientMarketingOptIn = requestData.marketingComms.patient ?? null;
      }
    } else {
      this.onboardingInfo.isMainMemberPatient = true;
      this.onboardingInfo.patientDetails = null;
    }

    if (requestData.guarantor?.addresses) {
      this.onboardingInfo.addressDetails = this.mapAddressesToAddressDetails(requestData.guarantor.addresses);
    } else {
      this.onboardingInfo.addressDetails = null;
    }

    if (requestData.nextOfKin) {
      this.onboardingInfo.nextOfKinDetails = this.mapNokToNokDetails(requestData.nextOfKin);
    } else {
      this.onboardingInfo.nextOfKinDetails = null;
    }

    this.onboardingInfo.paymentDetails = {
      paymentMethod: null,
      medicalSchemeName: null,
      medicalSchemeOption: null,
      medicalSchemeNumber: null,
      memberDependantNo: null,
      coidaClaimManager: null,
      coidaCompanyName: null,
      coidaDateOfInjury: null,
      coidaClaimNo: null,
    };

    if (requestData.insuranceType) {
      let paymentMethodValue: string | null = null;
      switch (requestData.insuranceType.toLowerCase()) {
        case 'medicalscheme':
        case 'medical scheme':
          paymentMethodValue = 'MedicalScheme';
          break;
        case 'private':
          paymentMethodValue = 'Private';
          break;
        case 'coida':
          paymentMethodValue = 'Coid';
          break;
        default:
          paymentMethodValue = requestData.insuranceType;
      }
      this.onboardingInfo.paymentDetails.paymentMethod = paymentMethodValue;

      if (paymentMethodValue === 'MedicalScheme' && requestData.medicalScheme) {
        this.onboardingInfo.paymentDetails.medicalSchemeName = requestData.medicalScheme.name ?? null;
        this.onboardingInfo.paymentDetails.medicalSchemeOption = requestData.medicalScheme.plan ?? null;
        this.onboardingInfo.paymentDetails.medicalSchemeNumber = requestData.medicalScheme.membershipNumber ?? null;
        this.onboardingInfo.paymentDetails.memberDependantNo = requestData.medicalScheme.dependantCode ?? null;
      } else if (paymentMethodValue === 'Coid' && requestData.coid) {
        this.onboardingInfo.paymentDetails.coidaClaimManager = requestData.coid.claimManager?.name ?? null;
        this.onboardingInfo.paymentDetails.coidaCompanyName = requestData.coid.employer?.name ?? null;
        this.onboardingInfo.paymentDetails.coidaDateOfInjury = this.formatDateToSlash(requestData.coid.dateOfInjury);
        this.onboardingInfo.paymentDetails.coidaClaimNo = requestData.coid.claimNumber ?? null;
      }
    }
    this._initialDataProcessed.next(true);
  }

  private mapGuarantorToPersonalDetails(guarantor: Guarantor): any {
    let derivedDob: string | null = null;
    const idNumber = guarantor.southAfricanIdNumber;
    if (idNumber && idNumber.length >= 6 && /^\d{6}/.test(idNumber)) {
      const yyStr = idNumber.substring(0, 2);
      const mmStr = idNumber.substring(2, 4);
      const ddStr = idNumber.substring(4, 6);
      const yy = parseInt(yyStr, 10);
      const mm = parseInt(mmStr, 10);
      const dd = parseInt(ddStr, 10);

      if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
        const currentYearLastTwoDigits = new Date().getFullYear() % 100;
        const fullYear = yy <= currentYearLastTwoDigits ? 2000 + yy : 1900 + yy;
        if (!isNaN(new Date(fullYear, mm - 1, dd).getFullYear())) {
          derivedDob = `${fullYear}/${mmStr}/${ddStr}`;
        }
      }
    }

    const mobileCountryCode = guarantor.telecoms?.mobile?.countryCode ?? this.DEFAULT_COUNTRY_CODE;
    const homeCountryCode = guarantor.telecoms?.home?.countryCode ?? this.DEFAULT_COUNTRY_CODE;
    const workCountryCode = guarantor.telecoms?.work?.countryCode ?? this.DEFAULT_COUNTRY_CODE;

    return {
      title: guarantor.title,
      firstName: guarantor.firstNames,
      surname: guarantor.surname,
      initials: guarantor.initials,
      sexAtBirth: null,
      language: guarantor.language,
      nationality: guarantor.nationality,
      idNo: guarantor.southAfricanIdNumber,
      passportNo: guarantor.passportNumber,
      dateOfBirth: derivedDob,
      communicationPreference: guarantor.communicationType,
      cellNumber: guarantor.telecoms?.mobile?.number ?? null,
      cellNumberCountryCode: mobileCountryCode,
      email: guarantor.email,
      landlineNumber: guarantor.telecoms?.home?.number ?? null,
      landlineNumberCountryCode: homeCountryCode,
      workNumber: guarantor.telecoms?.work?.number ?? null,
      workNumberCountryCode: workCountryCode,
      marketingOptIn: null,
    };
  }
  private mapPatientToPatientDetails(patient: Patient): PatientDetails {
    let patientIdType: 'id' | 'passport' | null = null;
    if (patient.southAfricanIdNumber) patientIdType = 'id';
    else if (patient.passportNumber) patientIdType = 'passport';

    let formattedDob: string | null = this.formatDateToSlash(patient.dateOfBirth);

    let code_val = this.DEFAULT_COUNTRY_CODE;
    if (patient.telecoms?.mobile?.countryCode)
      code_val = countryCodes.find((code) => code.code === patient.telecoms?.mobile?.countryCode)?.code ?? this.DEFAULT_COUNTRY_CODE;

    const mobileCountryCode = code_val;

    return {
      patientInitials: patient.initials ?? null,
      patientNationality: patient.nationality ?? null,
      patientFirstName: patient.firstNames ?? null,
      patientSurname: patient.surname ?? null,
      patientDateOfBirth: formattedDob,
      email: patient.email ?? null,
      sex: patient.sex ?? null,
      cellNumber: patient.telecoms?.mobile?.number ?? null,
      patientIdentifierType: patientIdType,
      patientIdNo: patient.southAfricanIdNumber ?? null,
      patientPassportNo: patient.passportNumber ?? null,
      cellNumberCountryCode: mobileCountryCode,
      patientMarketingOptIn: null,
      relationshipToGuarantor: patient.relationToMainMember ?? null,
    };
  }
  private mapAddressesToAddressDetails(addresses: Addresses | null | undefined): any {
    if (!addresses) {
      return {
        physicalLine1: null,
        physicalLine2: null,
        physicalSuburb: null,
        physicalTown: null,
        physicalPostalCode: null,
        physicalCountry: null,
        isSameAsPhysical: false,
        postalLine1: null,
        postalLine2: null,
        postalSuburb: null,
        postalTown: null,
        postalPostalCode: null,
        postalCountry: null,
      };
    }

    const resAddr = addresses.residential;
    const postAddr = addresses.postal;

    const isSame =
      !postAddr ||
      (resAddr &&
        postAddr &&
        resAddr.line1 === postAddr.line1 &&
        (resAddr.line2 || '') === (postAddr.line2 || '') &&
        (resAddr.suburb || '') === (postAddr.suburb || '') &&
        resAddr.town === postAddr.town &&
        resAddr.postalCode === postAddr.postalCode &&
        resAddr.countryCode === postAddr.countryCode);

    return {
      physicalLine1: resAddr?.line1 ?? null,
      physicalLine2: resAddr?.line2 ?? null,
      physicalSuburb: resAddr?.suburb ?? null,
      physicalTown: resAddr?.town ?? null,
      physicalPostalCode: resAddr?.postalCode ?? null,
      physicalCountry: resAddr?.countryCode ?? null,
      isSameAsPhysical: isSame,
      postalLine1: !isSame ? postAddr?.line1 ?? null : null,
      postalLine2: !isSame ? postAddr?.line2 ?? null : null,
      postalSuburb: !isSame ? postAddr?.suburb ?? null : null,
      postalTown: !isSame ? postAddr?.town ?? null : null,
      postalPostalCode: !isSame ? postAddr?.postalCode ?? null : null,
      postalCountry: !isSame ? postAddr?.countryCode ?? null : null,
    };
  }
  private mapNokToNokDetails(nok: NextOfKin): any {
    let code_val = this.DEFAULT_COUNTRY_CODE;
    if (nok.telecoms?.mobile?.countryCode)
      code_val = countryCodes.find((code) => code.code === nok.telecoms?.mobile?.countryCode)?.code ?? this.DEFAULT_COUNTRY_CODE;

    const mobileCountryCode = code_val;
    return {
      kinName: nok.name ?? null,
      kinRelationship: nok.relationship ?? null,
      kinTelNo: nok.telecoms?.mobile?.number ?? null,
      kinTelCountryCode: mobileCountryCode,
    };
  }

  public async getOnboardingData(): Promise<OnboardingDataModel> {
    if (!this.parentFhirId) {
      await this.GetHostConfig();
    }
    return JSON.parse(JSON.stringify(this.onboardingInfo));
  }
  public clearOnboardingData(): void {
    this.onboardingInfo = new OnboardingDataModel();
    this.currentStepIndex = 0;
    this.stepCompletion = Array(7).fill(true);
    this._unsubscribeAll.next();
    this._onboardingFormsCache = [];
    this._onboardingFormsLoaded.next([]);
    this.parentFhirId = '';
    this.logoUri = '';
    this.hostConfigPromise = null;
    localStorage.removeItem(this.HOST_PARENT_ORG_ID_KEY);
    this._initialDataProcessed.next(false);
  }
  public getCurrentStep(): number {
    return this.currentStepIndex;
  }
  goToNextStep(): void {
    if (this.currentStepIndex < this.stepCompletion.length - 1 && this.stepCompletion[this.currentStepIndex]) {
      this.currentStepIndex++;
      this._stepsChanged.next(this.currentStepIndex);
    }
  }
  goToPreviousStep(): void {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this._stepsChanged.next(this.currentStepIndex);
    }
  }
  goToStep(stepIndex: number): void {
    if (stepIndex >= 0 && stepIndex < this.stepCompletion.length) {
      this.currentStepIndex = stepIndex;
      this._stepsChanged.next(this.currentStepIndex);
    }
  }
  markStepAsComplete(): void {
    if (this.currentStepIndex >= 0 && this.currentStepIndex < this.stepCompletion.length) {
      this.stepCompletion[this.currentStepIndex] = true;
    }
  }
  markStepAsIncomplete(): void {
    if (this.currentStepIndex >= 0 && this.currentStepIndex < this.stepCompletion.length) {
      this.stepCompletion[this.currentStepIndex] = false;
    }
  }
  isStepComplete(): boolean {
    if (this.currentStepIndex >= 0 && this.currentStepIndex < this.stepCompletion.length) {
      return this.stepCompletion[this.currentStepIndex];
    }
    return false;
  }
  setAllowProceed(allow: boolean): void {
    if (this.currentStepIndex >= 0 && this.currentStepIndex < this.stepCompletion.length) {
      this.stepCompletion[this.currentStepIndex] = allow;
    }
  }
  public updateAddressDetails(addressDetails: any): void {
    this.onboardingInfo.addressDetails = { ...addressDetails };
  }
  public upadateIsMainMemberPatient(isMainMemberPatient: boolean): void {
    this.onboardingInfo.isMainMemberPatient = isMainMemberPatient;
  }
  public updateNextOfKinDetails(nextOfKinDetails: any): void {
    this.onboardingInfo.nextOfKinDetails = { ...nextOfKinDetails };
  }

  public updatePaymentDetails(paymentDetailsFromForm: any): void {
    this.onboardingInfo.paymentDetails = { ...paymentDetailsFromForm };
  }
  public async loadFormsByOrganization(): Promise<void> {
    const organizationId = this.parentFhirId;
    if (!organizationId) {
      this._onboardingFormsLoaded.next([]);
      return;
    }
    let requestModel: FormRequestModel = {
      parentOrganizationFhirId: organizationId,
      childOrganizationFhirId: null,
      healthCareServiceFhirId: null,
      locationFhirId: null,
      specialityFhirId: null,
    };
    try {
      const result = await firstValueFrom(
        this._healthFindrAnonymousService.anonymousFormsPost({ body: requestModel, isTemplate: false }).pipe(takeUntil(this._unsubscribeAll))
      );
      if (result && result.length > 0) {
        const pForms = result
          .map((f) => {
            try {
              const p = JSON.parse(f);
              return typeof p === 'object' && p !== null && p.formType && Array.isArray(p.fields) ? p : null;
            } catch (e) {
              return null;
            }
          })
          .filter((f): f is { formType: string; fields: any[]; sectionTitle?: string; orderIndex?: number } => f !== null);
        const oForms = pForms
          .filter((f) => f.formType === 'onboarding')
          .map((f) => ({
            sectionTitle: f.sectionTitle || 'Untitled Section',
            fields: this.transformToFormlyFields(f.fields),
            orderIndex: f.orderIndex ?? 0,
          }))
          .sort((a, b) => a.orderIndex - b.orderIndex);
        this._onboardingFormsCache = oForms;
        this._onboardingFormsLoaded.next([...oForms]);
      } else {
        this._onboardingFormsCache = [];
        this._onboardingFormsLoaded.next([]);
      }
    } catch (error) {
      this._onboardingFormsCache = [];
      this._onboardingFormsLoaded.next([]);
    }
  }
  public async getOnboardingForms(): Promise<OnboardingForm[]> {
    if (this._onboardingFormsCache.length > 0) {
      return Promise.resolve([...this._onboardingFormsCache]);
    }
    if (!this.parentFhirId) {
      try {
        await this.GetHostConfig();
      } catch (error) {
        return Promise.resolve([]);
      }
    }

    if (this.parentFhirId) {
      await this.loadFormsByOrganization();
      return Promise.resolve([...this._onboardingFormsCache]);
    } else {
      return Promise.resolve([]);
    }
  }
  public transformToFormlyFields(fields: any[]): FormlyFieldConfig[] {
    return Array.isArray(fields)
      ? fields.map((fieldDefinition: any) => {
          const props = fieldDefinition.templateOptions || {};
          let formlyType = fieldDefinition.type || 'customInput';

          switch (formlyType) {
            case 'label':
              formlyType = 'customLabel';
              break;
            case 'text':
              formlyType = 'customText';
              break;
            case 'divider':
              formlyType = 'customDivider';
              break;
            case 'input':
              formlyType = 'customInput';
              break;
            case 'textarea':
              formlyType = 'customTextarea';
              break;
            case 'select':
              formlyType = 'customSelect';
              break;
            case 'date':
              formlyType = 'customDate';
              break;
            case 'radio':
              formlyType = 'customRadio';
              break;
            case 'slider':
              formlyType = 'customSlider';
              break;
            case 'checkbox':
              formlyType = 'customCheckbox';
              break;
            default:
              formlyType = 'customInput';
          }

          return {
            key: fieldDefinition.key,
            type: formlyType,
            className: fieldDefinition.className || '',
            props: {
              ...props,
              label: props.label || '',
              placeholder: props.placeholder || '',
              required: props.required || false,
              options: props.options || [],
              text: props.text || '',
              type: props.type || 'text',
            },
            validation: fieldDefinition.validation,
            expressions: fieldDefinition.expressions,
            hooks: fieldDefinition.hooks,
          };
        })
      : [];
  }
  public updatePatientDetails(patientDetails: PatientDetails): void {
    this.onboardingInfo.patientDetails = { ...patientDetails };
  }
  public updatePersonalDetails(gaurentorDetails: any): void {
    this.onboardingInfo.gaurentorDetails = { ...gaurentorDetails };
  }
  public updateTermsAcceptance(
    termsAccepted: boolean,
    termsHtml: string | null,
    version: string | null,
    timestamp: Date | null,
    guarantorSig: string | null,
    patientSig: string | null,
    isPatientUnder16: boolean | null
  ): void {
    this.onboardingInfo.termsAccepted = termsAccepted;
    this.onboardingInfo.termsAndConditionsHtml = termsHtml;
    this.onboardingInfo.termsVersion = version;
    this.onboardingInfo.termsAcceptedAt = timestamp ? timestamp.toISOString() : null;
    this.onboardingInfo.guarantorSignatureDataUrl = guarantorSig;
    this.onboardingInfo.patientSignatureDataUrl = patientSig;
    this.onboardingInfo.isPatientUnder16 = isPatientUnder16;
  }
  public updateFormData(formData: { [key: string]: any }): void {
    this.onboardingInfo.formData = { ...this.onboardingInfo.formData, ...formData };
  }
  public fetchTermsAndConditions(
    parentOrgId: string | null,
    childOrgId?: string | null,
    hcsId?: string | null,
    locId?: string | null,
    specId?: string | null
  ): Observable<TermsConditionsModel | null> {
    if (!parentOrgId) {
      return of(null);
    }
    const reqBody: ConfigSearchRequestModel = {
      parentOrganizationFhirId: parentOrgId,
      childOrganizationFhirId: childOrgId || null,
      healthCareServiceFhirId: hcsId || null,
      locationFhirId: locId || null,
      specialityFhirId: specId || null,
    };
    return this._healthFindrAnonymousService.anonymousTermsConditionsPost({ body: reqBody }).pipe(
      map((termsList: TermsConditionsModel[]) => {
        if (!termsList || termsList.length === 0) {
          return null;
        }
        const validTerms = termsList.filter((term) => {
          if (!term.isActive) return false;
          try {
            const startDate = term.startDate ? new Date(term.startDate) : null;
            const endDate = term.endDate ? new Date(term.endDate) : null;
            if ((term.startDate && isNaN(Date.parse(term.startDate))) || (term.endDate && isNaN(Date.parse(term.endDate)))) {
              return false;
            }
            const now = new Date();
            const isAfterStartDate = startDate ? now >= startDate : true;
            const isBeforeEndDate = endDate ? now <= endDate : true;
            return isAfterStartDate && isBeforeEndDate;
          } catch (e) {
            return false;
          }
        });
        if (validTerms.length === 0) {
          return null;
        }
        validTerms.sort((a, b) => {
          const versionA = parseFloat(a.version || '0');
          const versionB = parseFloat(b.version || '0');
          if (!isNaN(versionA) && !isNaN(versionB) && versionB !== versionA) {
            return versionB - versionA;
          }
          const stringCompare = (b.version || '').localeCompare(a.version || '');
          if (stringCompare !== 0) return stringCompare;
          const dateA = a.lastUpdated ? new Date(a.lastUpdated) : new Date(0);
          const dateB = b.lastUpdated ? new Date(b.lastUpdated) : new Date(0);
          const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
          const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
          return timeB - timeA;
        });
        return validTerms[0];
      }),
      catchError((error) => {
        return of(null);
      })
    );
  }
  public getMedicalAidSchemes(): Observable<GroupedMedicalAidScheme[]> {
    return this.mpiDataService.medicalSchemesElixirLiveGet().pipe(
      map((dtos: MedicalSchemeDto[]): GroupedMedicalAidScheme[] => {
        if (!dtos || dtos.length === 0) return [];
        const validDtos = dtos.filter((dto) => dto.name && dto.plan);
        const groupedSchemes = validDtos.reduce((acc, currentDto) => {
          const schemeName = currentDto.name!;
          let group = acc.find((g) => g.name === schemeName);
          if (!group) {
            group = { name: schemeName, plans: [] };
            acc.push(group);
          }
          group.plans.push({ code: currentDto.code ?? null, planName: currentDto.plan ?? null });
          return acc;
        }, [] as GroupedMedicalAidScheme[]);
        groupedSchemes.sort((a, b) => a.name.localeCompare(b.name));
        groupedSchemes.forEach((scheme) => scheme.plans.sort((a, b) => (a.planName ?? '').localeCompare(b.planName ?? '')));
        return groupedSchemes;
      }),
      catchError((error) => {
        return of([]);
      })
    );
  }

  public getInsurers(): Observable<Insurer[]> {
    return this.mpiDataService.insurersElixirLiveGet().pipe(
      map((dtos: InsurerDto[]): Insurer[] => {
        if (!dtos || dtos.length === 0) return [];
        const validInsurers = dtos.filter((dto) => dto.name).map((dto): Insurer => ({ code: dto.code ?? null, name: dto.name ?? null }));
        validInsurers.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        return validInsurers;
      }),
      catchError((error) => {
        return of([]);
      })
    );
  }

  public getCoidClaimManagers(): Observable<CoidClaimManager[]> {
    return this.mpiDataService.coidClaimManagersElixirLiveGet().pipe(
      map((dtos: CoidClaimManagerDto[]): CoidClaimManager[] => {
        if (!dtos || dtos.length === 0) return [];
        const validManagers = dtos.filter((dto) => dto.name).map((dto): CoidClaimManager => ({ code: dto.code ?? null, name: dto.name ?? null }));
        validManagers.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        return validManagers;
      }),
      catchError((error) => {
        return of([]);
      })
    );
  }
  public async submitOnboarding(): Promise<boolean> {
    const authService = this.injector.get(AuthService);

    this._loadingService.setLoading(true, 'onboarding-submit');
    const prefillData = await firstValueFrom(authService.prefillData$.pipe(take(1)));
    const bookingId = prefillData?.bookRegistrationId;

    if (!bookingId) {
      this._loadingService.setLoading(false, 'onboarding-submit');
      return false;
    }

    const submissionPayload: PatientRegistrationRequestDto = this.mapOnboardingDataToSubmissionDto(this.onboardingInfo);

    try {
      const demoService = this.injector.get(DemoService);
      if (demoService.isEnabled) {
        // The demo complete endpoint skips Elixir Live and returns the payload and a PDF instead.
        await demoService.completeRegistration(bookingId, submissionPayload);
      } else {
        await firstValueFrom(
          bookCompleteBookRegistrationIdPost(this.http, this.mpiRootUrl, {
            bookRegistrationId: bookingId,
            body: submissionPayload,
          }).pipe(takeUntil(this._unsubscribeAll))
        );
      }
      this.clearOnboardingData();
      this._loadingService.setLoading(false, 'onboarding-submit');
      return true;
    } catch (error) {
      console.error('OnboardingService: Submission failed:', error);
      this._loadingService.setLoading(false, 'onboarding-submit');
      throw error;
    }
  }

  private getApiInsuranceType(paymentMethod: string | null | undefined): string | null {
    if (!paymentMethod) return null;
    const lowerPaymentMethod = paymentMethod.toLowerCase();
    switch (lowerPaymentMethod) {
      case 'medicalscheme':
        return 'MedicalScheme';
      case 'private':
        return 'Private';
      case 'coid':
        return 'Coid';
      case 'insurance':
        return 'Insurance';
      case 'company':
        return 'Company';
      default:
        return paymentMethod;
    }
  }

  private mapOnboardingDataToSubmissionDto(data: OnboardingDataModel): PatientRegistrationRequestDto {
    const layoutService = this.injector.get(LayoutService);

    const guarantorAddresses = this.reverseMapAddressDetails(data.addressDetails);
    const guarantor = this.mapPersonalDetailsToGuarantor(data.gaurentorDetails, guarantorAddresses);

    let patient: Patient | null = null;
    if (data.isMainMemberPatient) {
      patient = this.mapPersonalDetailsToPatient(data.gaurentorDetails, guarantorAddresses);
    } else {
      patient = this.mapPatientDetailsToPatient(data.patientDetails);
    }

    const nextOfKin = this.mapNokDetailsToNok(data.nextOfKinDetails);
    const guardian = data.isPatientUnder16 ? this.mapPersonalDetailsToGuardian(data.gaurentorDetails) : null;

    const paymentDetailsFromForm = data.paymentDetails;
    const apiInsuranceType = this.getApiInsuranceType(paymentDetailsFromForm?.paymentMethod);

    const medicalScheme = apiInsuranceType === 'MedicalScheme' ? this.mapPaymentToMedicalScheme(paymentDetailsFromForm) : null;
    const coid = apiInsuranceType === 'Coid' ? this.mapPaymentToCoid(paymentDetailsFromForm) : null;

    let determinedCompletedBy: string | null = null;
    const patientOption = completedByOptions.find((o) => o.value === 'Patient');
    const guardianOption = completedByOptions.find((o) => o.value === 'Guardian');

    if (data.isPatientUnder16 && guardian && guardianOption) {
      determinedCompletedBy = guardianOption.value;
    } else if (patientOption) {
      determinedCompletedBy = patientOption.value;
    }

    const req: PatientRegistrationRequest = {
      bookingId: data.bookRegistrationId ?? null,
      sourceInstance: data.sourceInstance ?? null,
      externalId: data.externalId ?? null,
      patient: patient,
      guarantor: guarantor,
      nextOfKin: nextOfKin,
      insuranceType: apiInsuranceType,
      medicalScheme: medicalScheme,
      coid: coid,
      document: null,
      documentFormat: null,
      marketingComms: {
        patient: data.isMainMemberPatient
          ? data.gaurentorDetails?.marketingCommsGaurantor ?? null
          : data.patientDetails?.patientMarketingOptIn ?? null,
        guarantor: data.gaurentorDetails?.marketingCommsGaurantor ?? null,
      },
      completedBy: determinedCompletedBy,
      guardian: guardian,
      completedOn: new Date().toISOString(),
    };

    const patientSignatureForDto = data.isMainMemberPatient ? data.guarantorSignatureDataUrl : data.patientSignatureDataUrl;

    const currentLogoUrl = this.parentFhirId
      ? `${environment.organizationBaseUrl}/Anonymous/${this.parentFhirId}/Image`
      : layoutService.logoUri || null;

const dto: PatientRegistrationRequestDto = {
    patientSignature: data.requireSignature ? (patientSignatureForDto ?? null) : null,
    gaurantorSignature: data.requireSignature ? (data.guarantorSignatureDataUrl ?? null) : null,
    termsAndConditions: data.termsAndConditionsHtml ?? null,
    termsAndConditionsVersion: data.termsVersion ?? null,
    signedOn: data.termsAcceptedAt ?? new Date().toISOString(),
    patientRegistrationRequest: req,
    logoUrl: currentLogoUrl,
  };

    return dto;
  }

  private mapPersonalDetailsToGuarantor(pd: any, addresses: Addresses | null): Guarantor {
    const guarantor: Guarantor = {
      title: pd?.title ?? null,
      initials: pd?.initials ?? null,
      firstNames: pd?.firstName ?? null,
      surname: pd?.surname ?? null,
      nationality: pd?.nationality ?? null,
      southAfricanIdNumber: pd?.nationality === 'ZA' ? pd?.idNo || null : null,
      passportNumber: pd?.nationality === 'ZA' ? null : pd?.passportNo || null,
      telecoms: {
        mobile: this.buildTelecomEntry(pd?.cellNumber, pd?.cellNumberCountryCode, true),
        home: this.buildTelecomEntry(pd?.landlineNumber, pd?.landlineNumberCountryCode, false),
        work: this.buildTelecomEntry(pd?.workNumber, pd?.workNumberCountryCode, false),
      },
      addresses: addresses,
      email: pd?.email ?? null,
      communicationOptIn: pd?.marketingOptIn ?? null,
      language: pd?.language ?? null,
      communicationType: pd?.communicationPreference ?? null,
      employer: null,
    };
    return guarantor;
  }

  private mapPersonalDetailsToPatient(pd: any, addresses: Addresses | null): Patient {
     const dob = pd?.dateOfBirth ? this.formatDateToDash(pd.dateOfBirth) : null;
    const selfRelationOption = relationToMainMemberOptions.find((o) => o.value === 'Self');

    // Guarantor shows ID vs Passport based on nationality (ZA = ID). Changing nationality
    // does not clear the other field on the form, so key off nationality here and null the
    // field that doesn't apply, preventing a stale ID/passport from reaching Elixir.
    const isZa = pd?.nationality === 'ZA';
    const southAfricanIdNumber = isZa ? pd?.idNo || null : null;
    const passportNumber = isZa ? null : pd?.passportNo || null;

    const patient: Patient = {
      title: pd?.title ?? null,
      initials: pd?.initials ?? null,
      firstNames: pd?.firstName ?? null,
      surname: pd?.surname ?? null,
      nationality: pd?.nationality ?? null,
      southAfricanIdNumber: southAfricanIdNumber,
      passportNumber: passportNumber,
      telecoms: {
        mobile: this.buildTelecomEntry(pd?.cellNumber, pd?.cellNumberCountryCode, true),
        home: this.buildTelecomEntry(pd?.landlineNumber, pd?.landlineNumberCountryCode, false),
      },
      addresses: addresses,
      email: pd?.email ?? null,
      communicationOptIn: pd?.marketingOptIn ?? null,
      dateOfBirth: dob,
      sex: pd?.sexAtBirth ?? null,
      relationToMainMember: selfRelationOption ? selfRelationOption.value : 'Self',
    };
    return patient;
  }

  private mapPatientDetailsToPatient(pd: PatientDetails | null): Patient {
    if (!pd) return {} as Patient;

    const dtoDob = pd?.patientDateOfBirth ? this.formatDateToDash(pd.patientDateOfBirth) : null;

    // Only carry the identifier that matches the selected type; force the other to null
    // so a value left over from switching SA ID <-> Passport can never leak through.
    const isPassport = pd?.patientIdentifierType === 'passport';
    const southAfricanIdNumber = isPassport ? null : pd?.patientIdNo || null;
    const passportNumber = isPassport ? pd?.patientPassportNo || null : null;

    const patient: Patient = {
      title: null,
      initials: pd?.patientInitials ?? null,
      firstNames: pd?.patientFirstName ?? null,
      surname: pd?.patientSurname ?? null,
      nationality: pd?.patientNationality ?? null,
      southAfricanIdNumber: southAfricanIdNumber,
      passportNumber: passportNumber,
      telecoms: {
        mobile: this.buildTelecomEntry(pd?.cellNumber, pd?.cellNumberCountryCode, true),
        home: this.buildTelecomEntry(null, null, false),
      },
      addresses: null,
      email: pd?.email ?? null,
      communicationOptIn: pd?.patientMarketingOptIn ?? null,
      dateOfBirth: dtoDob,
      sex: pd?.sex ?? null,
      relationToMainMember: pd?.relationshipToGuarantor ?? null,
    };
    return patient;
  }

  private mapNokDetailsToNok(nd: any): NextOfKin {
    const nok: NextOfKin = {
      name: nd?.kinName ?? null,
      relationship: nd?.kinRelationship ?? null,
      telecoms: {
        mobile: this.buildTelecomEntry(nd?.kinTelNo, nd?.kinTelCountryCode, true),
        home: this.buildTelecomEntry(null, null, false),
      },
      addresses: null,
    };
    return nok;
  }

  private createEmptyAddress(): Address {
    return {
      countryCode: null,
      line1: null,
      line2: null,
      suburb: null,
      town: null,
      postalCode: null,
    };
  }

  private reverseMapAddress(
    line1: string | null,
    line2: string | null,
    suburb: string | null,
    town: string | null,
    postalCode: string | null,
    countryCode: string | null
  ): Address {
    return {
      line1: line1 ?? null,
      line2: line2 ?? null,
      suburb: suburb ?? null,
      town: town ?? null,
      postalCode: postalCode ?? null,
      countryCode: countryCode ?? null,
    };
  }

  private reverseMapAddressDetails(ad: any): Addresses | null {
    if (!ad) {
      return null;
    }

    const residentialAddress = this.createMeaningfulAddress(
      ad.physicalLine1,
      ad.physicalLine2,
      ad.physicalSuburb,
      ad.physicalTown,
      ad.physicalPostalCode,
      ad.physicalCountry
    );

    const postalAddress = ad.isSameAsPhysical
      ? residentialAddress
      : this.createMeaningfulAddress(ad.postalLine1, ad.postalLine2, ad.postalSuburb, ad.postalTown, ad.postalPostalCode, ad.postalCountry);

    if (!residentialAddress && !postalAddress) {
      return null;
    }

    return {
      residential: residentialAddress,
      postal: postalAddress,
      work: null,
    };
  }

  private mapPaymentToMedicalScheme(pd: any): MedicalScheme | null {
    if (pd?.paymentMethod?.toLowerCase() !== 'medicalscheme') return null;

    return {
      code: pd.medicalSchemeCode ?? null,
      name: pd.medicalSchemeName ?? null,
      plan: pd.medicalSchemeOption ?? null,
      membershipNumber: pd.medicalSchemeNumber ?? null,
      dependantCode: pd.memberDependantNo ?? null,
    };
  }

  private mapPaymentToCoid(pd: any): Coid | null {
    if (pd?.paymentMethod?.toLowerCase() !== 'coid') return null;
    return {
      claimManager: {
        name: pd.coidaClaimManager ?? null,
      },
      employer: {
        name: pd.coidaCompanyName ?? null,
        registrationNumber: null,
      },
      claimNumber: pd.coidaClaimNo ?? null,
      dateOfInjury: this.formatDateToDash(pd.coidaDateOfInjury),
    };
  }

  private mapPaymentToCompany(pd: any): Company | null {
    if (pd?.paymentMethod?.toLowerCase() !== 'company') return null;
    return {
      name: pd.companyName ?? null,
      registrationNumber: pd.companyRegNo ?? null,
      vatNumber: pd.companyVatNo ?? null,
      contactPerson: pd.companyContactPerson ?? null,
      email: pd.companyEmail ?? null,
      contactNumber: pd.companyContactNo ?? null,
    };
  }
  private createMeaningfulAddress(
    line1: string | null,
    line2: string | null,
    suburb: string | null,
    town: string | null,
    postalCode: string | null,
    countryCode: string | null
  ): Address | null {
    if (line1 && suburb && postalCode && town && countryCode) {
      return {
        line1: line1,
        line2: line2,
        suburb: suburb,
        town: town,
        postalCode: postalCode,
        countryCode: countryCode,
      };
    }
    return null;
  }
  private mapPaymentToInsurance(pd: any): Insurance | null {
    if (pd?.paymentMethod?.toLowerCase() !== 'insurance') return null;

    const insurerDetail = pd.insuranceInsurer;
    let insurerCode: string | null = null;
    let insurerName: string | null = null;

    if (insurerDetail && typeof insurerDetail === 'object' && insurerDetail !== null) {
      insurerCode = insurerDetail.code ?? null;
      insurerName = insurerDetail.name ?? null;
    } else if (typeof insurerDetail === 'string') {
      insurerName = insurerDetail;
    }

    return {
      insurer: {
        code: insurerCode,
        name: insurerName,
        policyNumber: pd.insurancePolicyNo ?? null,
      },
      broker: {
        name: pd.insuranceBrokerName ?? null,
        contactNumber: pd.insuranceContactNo ?? null,
        email: pd.insuranceEmail ?? null,
      },
    };
  }

  private mapPersonalDetailsToGuardian(pd: any): Gaurdian {
    const fullName = pd?.firstName && pd?.surname ? `${pd.firstName} ${pd.surname}`.trim() : null;
    return {
      fullName: fullName,
      southAfricanIdNumber: pd?.idNo || null,
    };
  }

  private buildTelecomEntry(numberVal: string | null | undefined, countryCodeVal: string | null | undefined, isMobile: boolean): any {
    let num = numberVal?.trim() || null;
    if (!num) {
      return null;
    }

    const processingCountryCode = countryCodeVal?.trim().toUpperCase() || this.DEFAULT_COUNTRY_CODE;

    num = num.replace(/\D/g, '');

    if (processingCountryCode === 'ZA' && num.startsWith('0')) {
      num = num.substring(1);
    }

    const finalCountryCodeForPayload = processingCountryCode;

    return {
      number: num,
      countryCode: finalCountryCodeForPayload,
    };
  }
}
